// @flow

import React from "react";
import { localize } from "redux-i18n";
import { View, CheckboxHierarchyTree } from "@components";
import HierarchyScopeAreasIncluded from "./hierarchy-scope-areas-included";
import HierarchyScopeAreasExcluded from "./hierarchy-scope-areas-excluded";
import type { HierarchyAssignment } from "@modules/service/types";
import type {
  HierarchyTree,
  HierarchyTreeSelectedItem,
} from "@modules/shared/reducer/hierarchy/types";
import "./hierarchy-scope.scss";
import * as transformers from "./transformers";

type OwnProps = $Exact<{
  hierarchy: HierarchyTree,
  assignments: Array<HierarchyAssignment>,
  onChange: (assignments: Array<HierarchyAssignment>) => void,
}>;

type Props = OwnProps & PropsFromLocalize;

function HierarchyScopeForm(props: Props) {
  function getNodeName(id: number, tree: HierarchyTree = props.hierarchy): string {
    if (tree.data.id === id) {
      return tree.data.label;
    }

    return tree.data
      ? tree.children.reduce((acc: string, c: *) => acc || getNodeName(id, c), "")
      : "";
  }

  function getIncludedAreaData(assignment) {
    let activeFrom = new Date().toISOString();
    let activeTo = null;

    const currentAssignment = props.assignments.find(a => a.target.id === assignment.id);
    if (currentAssignment) {
      activeFrom = currentAssignment.activeFrom || activeFrom;
      activeTo = currentAssignment.activeTo || activeTo;
    }

    const data = {
      activeFrom,
      activeTo,
      isCascading: true,
      target: {
        id: assignment.id,
        label: getNodeName(assignment.id),
        type: "ORGANISATIONAL_UNIT",
      },
    };

    return [data, currentAssignment];
  }

  function getExlcudedAreaData(currentAssignment, excludedAreas) {
    return excludedAreas.map(exclusionId => {
      let activeFromExcluded = new Date().toISOString();
      let activeToExcluded = null;

      // preserve the activeFrom/To date of existing exclusion that is not removed
      if (currentAssignment && currentAssignment.exclusions) {
        const existExclusion = currentAssignment.exclusions.find(
          ee => ee.target.id === exclusionId,
        );

        if (existExclusion) {
          activeFromExcluded = existExclusion.activeFrom || activeFromExcluded;
          activeToExcluded = existExclusion.activeTo || activeToExcluded;
        }
      }

      return {
        activeFrom: activeFromExcluded,
        activeTo: activeToExcluded,
        isCascading: true,
        target: {
          id: exclusionId,
          label: getNodeName(exclusionId),
          type: "ORGANISATIONAL_UNIT",
        },
      };
    });
  }

  function handleHierarchyChange(selected: Array<HierarchyTreeSelectedItem>) {
    const hierarchyData = selected.map(assignment => {
      const [data, currentAssignment] = getIncludedAreaData(assignment);

      return {
        ...data,
        exclusions: assignment.excluded.length
          ? getExlcudedAreaData(currentAssignment, assignment.excluded)
          : [],
      };
    });

    props.onChange(hierarchyData);
  }

  function onChangeIncluded(assignments: Array<Object>) {
    const newAssignments = assignments.map(assignment => {
      const find = props.assignments.find(a => a.target.label === assignment.target);

      if (find) {
        return {
          ...find,
          activeFrom: assignment.activeFrom,
          activeTo: assignment.activeTo,
        };
      }

      // $FlowIgnore
      return assignment;
    });

    props && props.onChange && props.onChange(newAssignments);
  }

  function onChangeExcluded(exclusions: Array<Object>) {
    const exclusionsIds = exclusions.map(ex => ex.id);
    const newExclusions = props.assignments.map(assignment => {
      return {
        ...assignment,
        exclusions: assignment.exclusions.filter(
          exclusion => exclusionsIds.indexOf(exclusion.target.id) > -1,
        ),
      };
    });
    props && props.onChange && props.onChange(newExclusions);
  }

  if (!props.assignments) return null;
  return (
    <View height="560px" direction="row" justifyContent="space-between" gap={4} flex={1}>
      <View overflowY="auto" flex="1" separator="right" padding={[0, 4, 0, 0]}>
        {props.hierarchy && props.assignments && (
          <CheckboxHierarchyTree
            tree={props.hierarchy}
            expanded={[]}
            disabled={false}
            selected={transformers.getSelectedIds(props.assignments)}
            excluded={transformers.getExcludedIds(props.assignments)}
            onChange={handleHierarchyChange}
            searchable={true}
            searchPlaceholder={props.t("HIERARCHY_SCOPE_FIND_DEPARTMENTS")}
          />
        )}
      </View>
      <View overflowY="auto" flex="1" gap={10}>
        <HierarchyScopeAreasIncluded
          editing={true}
          assignments={props.assignments}
          onChange={onChangeIncluded}
        />

        <HierarchyScopeAreasExcluded
          editing={true}
          assignments={props.assignments}
          onChange={onChangeExcluded}
        />
      </View>
    </View>
  );
}

export default localize()(HierarchyScopeForm);
